import { useParams } from 'wouter';
import { AdminMeetings } from '@platform/client';
import {
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  SortingState,
  useReactTable,
  ColumnFiltersState,
} from '@tanstack/react-table';
import { Table, Button, IconButton } from '@platform/components';
import { AdminRegistrationDetail } from '@platform/client/schemas';
import { useMemo, useState, useContext } from 'react';
import { useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { formatFilter } from '../../../util/FormatFilterState';
import { toSnakeCase } from '../../../util/SnakeCase';
import { CSVLink } from 'react-csv';
import { AdminTableContext } from '../../AdminTable';

const columnHelper = createColumnHelper<AdminRegistrationDetail>();

export const CommunityMeetingDetail = () => {
  const { meetingId } = useParams();
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [downloadButtonStage, setDownloadButtonState] = useState<'export' | 'download'>('export');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);

  const {
    data: meetingData,
    isSuccess,
    isLoading,
    isError,
  } = AdminMeetings.useGetCommunityMeetingDetail(
    {
      meeting_id: Number(meetingId),
      filter: formatFilter(columnFilters),
      page: pagination.pageIndex,
      limit: pagination.pageSize,
      sort: sorting.map((s) => `${toSnakeCase(s.id)}:${s.desc ? 'DESC' : 'ASC'}`).join(','),
    },
    { query: { placeholderData: keepPreviousData } },
  );
  const registrations = meetingData?.registrations || [];

  const queryClient = useQueryClient();
  const updateAllAttendanceMutator = AdminMeetings.useRefreshMeeting({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: AdminMeetings.getGetMeetingsQueryKey({
            ...adminQueryOptionsV2,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: AdminMeetings.getGetCommunityMeetingDetailQueryKey({
            meeting_id: Number(meetingId),
            filter: formatFilter(columnFilters),
            page: pagination.pageIndex,
            limit: pagination.pageSize,
            sort: sorting.map((s) => `${toSnakeCase(s.id)}:${s.desc ? 'DESC' : 'ASC'}`).join(','),
          }),
        });
        setIsRefreshing(false);
      },
      onError: () => {
        alert('Error refreshing attendance');
        setIsRefreshing(false);
      },
    },
  });
  const updateSingleAttendanceMutator = AdminMeetings.useUpdateAttendance({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: AdminMeetings.getGetCommunityMeetingDetailQueryKey({
            meeting_id: Number(meetingId),
            filter: formatFilter(columnFilters),
            page: pagination.pageIndex,
            limit: pagination.pageSize,
            sort: sorting.map((s) => `${toSnakeCase(s.id)}:${s.desc ? 'DESC' : 'ASC'}`).join(','),
          }),
        });
      },
      onError: () => {
        alert('Error refreshing attendance');
      },
    },
  });

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => info.getValue(),
        meta: {
          filterVariant: 'text',
        },
      }),
      columnHelper.accessor('email', {
        header: 'Email',
        cell: (info) => info.getValue(),
        meta: {
          filterVariant: 'text',
        },
      }),
      columnHelper.accessor('attended', {
        header: 'Attended',
        cell: ({ row }) => {
          const attended = row.getValue('attended');
          return (
            <div className='flex items-center justify-between'>
              {attended ? 'Yes' : 'No'}
              <IconButton
                variant={attended ? 'secondary-danger' : 'secondary-color'}
                icon={attended ? 'x' : 'check'}
                size='sm'
                onPress={() => {
                  updateSingleAttendanceMutator.mutate({
                    params: {
                      meeting_registrant_id: row.original.id,
                      attended: !attended,
                    },
                  });
                }}
                isDisabled={isRefreshing}
              />
            </div>
          );
        },
        meta: {
          filterVariant: 'text',
        },
        size: 25,
      }),
      columnHelper.accessor('createdAt', {
        header: 'Registered',
        cell: (info) => new Date(info.getValue()).toLocaleDateString(),
        enableColumnFilter: false,
      }),
    ] as ColumnDef<AdminRegistrationDetail>[];
  }, [updateSingleAttendanceMutator, isRefreshing]);

  const adminMeetingDetailTable = useReactTable({
    data: registrations,
    rowCount: meetingData?.filteredRegistrationsCount || 0,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    onPaginationChange: setPagination,
    manualFiltering: true,
    onColumnFiltersChange: setColumnFilters,
    manualSorting: true,
    onSortingChange: setSorting,
    state: {
      pagination,
      sorting,
      columnFilters,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
      },
    },
  });

  return (
    <div className='max-w-5xl mx-auto '>
      {isLoading && <div className='text-center'>Loading...</div>}
      {isError && <div className='text-center text-error-500'>Error fetching meeting details</div>}
      {isSuccess && (
        <div className='flex flex-col gap-4 justify-center'>
          <div className='flex justify-between'>
            <Button variant='tertiary-gray' size='sm' href='/'>
              Back to meetings
            </Button>
            <div className='flex gap-2'>
              {meetingData.registrationRequired && (
                <Button
                  variant='secondary-color'
                  size='sm'
                  onPress={() => {
                    setIsRefreshing(true);
                    updateAllAttendanceMutator.mutate({
                      params: { zoom_id: meetingData.zoomId },
                    });
                  }}
                  isDisabled={isRefreshing}
                >
                  Refresh Registrations
                </Button>
              )}
              {downloadButtonStage === 'export' && (
                <Button
                  variant='tertiary-color'
                  size='sm'
                  onPress={() => setDownloadButtonState('download')}
                  isDisabled={isRefreshing}
                >
                  Create CSV
                </Button>
              )}
              {downloadButtonStage === 'download' && (
                <DownloadCSVButton
                  meetingId={Number(meetingData.id)}
                  zoomMeetingId={meetingData.zoomId}
                  setButtonStage={setDownloadButtonState}
                  filter={columnFilters}
                />
              )}
            </div>
          </div>
          <div>
            <h2 className='font-bold text-lg'>Meeting</h2>
            <div>
              <span className='font-semibold'>Name:</span> {meetingData.title}
            </div>
            <div>
              <span className='font-semibold'>Start Time:</span>{' '}
              {new Date(meetingData.startTime).toLocaleString()}
            </div>
            <div>
              <span className='font-semibold'>Host:</span> {meetingData.zoomHost}
            </div>
            <div>
              <span className='font-semibold'>Join url:</span>{' '}
              <a
                href={meetingData.joinUrl}
                target='_blank'
                className='hover:underline text-brand-500'
              >
                {meetingData.joinUrl}
              </a>
            </div>
          </div>
          <div>
            {meetingData.registrationRequired || registrations.length > 0 ?
              <div>
                <div className='flex gap-2 items-center'>
                  <h2 className='font-bold text-lg'>Registrations</h2>
                  {isRefreshing ?
                    <div className='text-success-500 text-sm'>
                      Refreshing from external API, this may take a few minutes...
                    </div>
                  : <span className='text-sm'>{`${meetingData.attended} attended of ${meetingData.registered} registered`}</span>
                  }
                </div>
                <Table table={adminMeetingDetailTable} />
              </div>
            : <div>
                Registration is not enabled for this meeting, attendance cannot be reported.
              </div>
            }
          </div>
        </div>
      )}
    </div>
  );
};

export const DownloadCSVButton = ({
  meetingId,
  zoomMeetingId,
  setButtonStage,
  filter,
}: {
  meetingId: number;
  zoomMeetingId: string;
  setButtonStage: React.Dispatch<React.SetStateAction<'export' | 'download'>>;
  filter: ColumnFiltersState;
}) => {
  const { data, isLoading, isSuccess, isError } = AdminMeetings.useGetRegistrationsDownloadData({
    meeting_id: meetingId,
    filter: formatFilter(filter),
  });
  if (isError) {
    return <div className='text-center text-error-500'>Error fetching download data</div>;
  }
  if (!data) {
    return null;
  }
  return (
    <Button variant='primary' size='sm' isDisabled={!isSuccess}>
      <CSVLink
        filename={`${zoomMeetingId}_registration_data`}
        data={data as object[]}
        onClick={() => setButtonStage('export')}
      >
        {isLoading ? 'Creating CSV' : 'Download CSV'}
      </CSVLink>
    </Button>
  );
};
